/**
 * Embed Voice Settings Schema
 *
 * The embed web widget "call" feature: a visitor can talk to the agent in the
 * browser (no phone number), reusing the realtime voice pipeline.
 *
 * Extends the shared VoiceConfig (model/voice/turn-detection/session) with an
 * `enabled` flag the Nexus user toggles per embed deployment.
 */

import { z } from "zod";

import { DEFAULT_VOICE_CONFIG, VoiceConfigSchema } from "../VoiceConfig.schema";

export const EmbedVoiceSettingsSchema = VoiceConfigSchema.extend({
  /** Whether the "call the agent" option is offered in this embed widget. */
  enabled: z.boolean()
});

export type EmbedVoiceSettings = z.infer<typeof EmbedVoiceSettingsSchema>;

export const DEFAULT_EMBED_VOICE_SETTINGS: EmbedVoiceSettings = {
  ...DEFAULT_VOICE_CONFIG,
  enabled: false
};

const {
  geminiModel: _newDeploymentModel,
  greetingPlayback: _newDeploymentGreetingPlayback,
  openaiModel: _newDeploymentOpenaiModel,
  openaiSettings: _newDeploymentOpenaiSettings,
  openaiTurnDetection: _newDeploymentOpenaiTurnDetection,
  ...STORED_EMBED_VOICE_SETTINGS_FILL
} = DEFAULT_EMBED_VOICE_SETTINGS;

/**
 * What a deployment's `voiceSettings` is filled from before it is edited or
 * saved — every reader that completes a voice config over defaults goes through
 * this, so a fill never changes which model a deployment runs on.
 *
 * A config that does not exist yet is born here, so it gets everything a new
 * deployment gets. A STORED config gets every default EXCEPT the five below,
 * for two different reasons.
 *
 * `geminiModel` and `greetingPlayback`: an absent one is how every row written
 * before the field existed reads, and it resolves to
 * `GEMINI_LIVE_MODEL_FOR_UNSET_DEPLOYMENTS` / `GREETING_PLAYBACK_FOR_UNSET_DEPLOYMENTS`
 * on every call. Filling it with the new-deployment value would change how that
 * row's calls run at its next save, without anyone choosing it.
 *
 * `openaiModel`, `openaiSettings` and `openaiTurnDetection`: exactly one
 * provider's keys are read on a call, picked by `provider`, so filling the
 * OpenAI ones persists three keys a Gemini or GPT-Live deployment never uses —
 * on every save, for every row the console loads. Every reader already falls
 * back when they are absent (`buildVoiceSessionConfig`'s `?? DEFAULT_OPENAI_*`,
 * `useVoiceIdentitySettings`, `useOpenaiVoiceSettings`, `useTurnDetectionSettings`),
 * which is the same argument `DEFAULT_VOICE_CONFIG` already makes by carrying no
 * `geminiTurnDetection` at all.
 */
export function embedVoiceSettingsFillFor(stored: unknown): EmbedVoiceSettings {
  return stored === undefined ? DEFAULT_EMBED_VOICE_SETTINGS : STORED_EMBED_VOICE_SETTINGS_FILL;
}
